import { useEffect } from 'react';

interface DynamicMetaTagsProps {
  title?: string;
  description?: string;
  canonical?: string;
  ogImage?: string;
  noIndex?: boolean; // For admin pages
  isAdminPage?: boolean;
}

const DynamicMetaTags: React.FC<DynamicMetaTagsProps> = ({
  title,
  description,
  canonical,
  ogImage,
  noIndex = false,
  isAdminPage = false
}) => {
  useEffect(() => {
    const keepOnlyFirst = (selector: string) => {
      const nodes = document.querySelectorAll(selector);
      nodes.forEach((node, index) => {
        if (index > 0) {
          node.parentElement?.removeChild(node);
        }
      });
    };

    const getOrCreateMeta = (
      selector: string,
      attributeName: 'name' | 'property',
      attributeValue: string
    ) => {
      keepOnlyFirst(selector);
      let metaTag = document.querySelector(selector) as HTMLMetaElement | null;
      if (!metaTag) {
        metaTag = document.createElement('meta');
        metaTag.setAttribute(attributeName, attributeValue);
        document.head.appendChild(metaTag);
      }
      return metaTag;
    };

    // Update document title
    if (title) {
      document.title = title;
    }
    
    // Update meta description
    if (description) {
      const metaDescription = document.querySelector('meta[name="description"]');
      if (metaDescription) {
        metaDescription.setAttribute('content', description);
      }
    }
    
    // Handle canonical URL (always set for SEO)
    const canonicalUrl = canonical || `https://aizen.tips${window.location.pathname}`;
    keepOnlyFirst('link[rel="canonical"]');
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.setAttribute('href', canonicalUrl);
    
    // Handle noindex for admin pages
    let robotsMetaTag = document.querySelector('meta[name="robots"]');
    if (noIndex || isAdminPage) {
      if (!robotsMetaTag) {
        robotsMetaTag = document.createElement('meta');
        robotsMetaTag.setAttribute('name', 'robots');
        document.head.appendChild(robotsMetaTag);
      }
      robotsMetaTag.setAttribute('content', 'noindex, nofollow');
    } else if (robotsMetaTag) {
      // Remove noindex if it exists and we don't want it
      robotsMetaTag.remove();
    }
    
    // Update Open Graph tags
    if (title) {
      const ogTitle = getOrCreateMeta('meta[property="og:title"]', 'property', 'og:title');
      ogTitle.setAttribute('content', title);
    }
    
    if (description) {
      const ogDescription = getOrCreateMeta('meta[property="og:description"]', 'property', 'og:description');
      ogDescription.setAttribute('content', description);
    }
    
    if (ogImage) {
      const ogImageTag = getOrCreateMeta('meta[property="og:image"]', 'property', 'og:image');
      ogImageTag.setAttribute('content', ogImage);
    }

    // Ensure og:url matches canonical
    const ogUrl = getOrCreateMeta('meta[property="og:url"]', 'property', 'og:url');
    ogUrl.setAttribute('content', canonicalUrl);

    // Update Twitter card tags
    if (title) {
      const twitterTitle = getOrCreateMeta('meta[name="twitter:title"]', 'name', 'twitter:title');
      twitterTitle.setAttribute('content', title);
    }
    if (description) {
      const twitterDescription = getOrCreateMeta('meta[name="twitter:description"]', 'name', 'twitter:description');
      twitterDescription.setAttribute('content', description);
    }
    if (ogImage) {
      const twitterImage = getOrCreateMeta('meta[name="twitter:image"]', 'name', 'twitter:image');
      twitterImage.setAttribute('content', ogImage);
    }
  }, [title, description, canonical, ogImage, noIndex, isAdminPage]);
  
  return null;
};

export default DynamicMetaTags; 